import { type FC, useEffect, useRef, useState } from 'react';
import { exportToJSON, exportToPDF, exportToPNG } from '@/services/exportCanvas';
import { useCanvasStore } from '@/stores/canvas.store';

export const ExportMenu: FC = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<'png' | 'pdf' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const canvas = useCanvasStore((s) => s.canvas);
  const nodes = useCanvasStore((s) => s.nodes);
  const edges = useCanvasStore((s) => s.edges);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const getFlowRoot = (): HTMLElement | null =>
    document.querySelector<HTMLElement>('.react-flow');

  const handlePng = async () => {
    if (!canvas) return;
    const root = getFlowRoot();
    if (!root) {
      setError('Zone canvas introuvable (vue non-canvas ?)');
      return;
    }
    setOpen(false);
    setBusy('png');
    setError(null);
    try {
      await exportToPNG(canvas.name, root);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec export PNG');
    } finally {
      setBusy(null);
    }
  };

  const handlePdf = async () => {
    if (!canvas) return;
    const root = getFlowRoot();
    if (!root) {
      setError('Zone canvas introuvable (vue non-canvas ?)');
      return;
    }
    setOpen(false);
    setBusy('pdf');
    setError(null);
    try {
      await exportToPDF(canvas.name, root);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec export PDF');
    } finally {
      setBusy(null);
    }
  };

  const handleJson = () => {
    if (!canvas) return;
    setOpen(false);
    exportToJSON(canvas, nodes, edges);
  };

  const item: React.CSSProperties = {
    display: 'block',
    width: '100%',
    padding: '8px 12px',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    color: 'var(--color-text)',
    cursor: 'pointer',
    fontSize: 'var(--font-size-sm)',
  };

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={!canvas || busy !== null}
        title="Exporter le canvas"
        style={{
          padding: 'var(--spacing-xs) var(--spacing-sm)',
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-text)',
          cursor: canvas && busy === null ? 'pointer' : 'not-allowed',
          fontSize: 'var(--font-size-sm)',
          opacity: canvas && busy === null ? 1 : 0.5,
        }}
      >
        {busy === 'png' ? 'Export PNG…' : busy === 'pdf' ? 'Export PDF…' : 'Exporter ▾'}
      </button>

      {error && (
        <div
          role="alert"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 4px)',
            right: 0,
            minWidth: 200,
            padding: '6px 10px',
            fontSize: 'var(--font-size-xs)',
            color: '#EF4444',
            backgroundColor: 'var(--color-surface)',
            border: '1px solid #EF4444',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            zIndex: 51,
          }}
          onClick={() => setError(null)}
        >
          {error}
        </div>
      )}

      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 4px)',
            right: 0,
            minWidth: 160,
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 50,
            overflow: 'hidden',
          }}
        >
          <button
            style={item}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-background)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            onClick={handlePng}
          >
            PNG (image)
          </button>
          <button
            style={item}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-background)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            onClick={handlePdf}
          >
            PDF (A4 paysage)
          </button>
          <button
            style={item}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-background)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            onClick={handleJson}
          >
            JSON (portable)
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportMenu;
